import React from 'react';
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  Typography,
  Box,
  Avatar,
  Chip,
  Tooltip
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  Domain as DomainIcon,
  Pending as PendingIcon,
  Assessment as AssessmentIcon,
  People as PeopleIcon,
  Settings as SettingsIcon,
  Storage as StorageIcon,
  TrendingUp as TrendingUpIcon,
  Logout as LogoutIcon,
  BugReport as ScraperIcon,
  SupervisedUserCircle as FollowersIcon,
  Visibility as VisibilityIcon,
  AutoAwesome as SentimentIcon,
  Key as KeyIcon,
  Public as GlobalIcon,
  Source as SourceIcon,
  Category as CategoryIcon,
  Business as BrandIcon2,
  Palette as PaletteIcon,
  Computer as ComputerIcon,
  Layers as TierIcon
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';

const AdminNavigation = ({ open, drawerWidth = 240, miniDrawerWidth = 60 }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const menuItems = [
    {
      text: 'Dashboard',
      icon: <DashboardIcon />,
      path: '/admin/dashboard',
      roles: ['admin', 'mensure']
    },
    {
      text: 'Sistema',
      icon: <ComputerIcon />,
      path: '/admin/system',
      roles: ['admin', 'mensure']
    },
    {
      text: 'Domínios Pendentes',
      icon: <PendingIcon />,
      path: '/admin/pending-domains',
      roles: ['admin', 'mensure']
    },
    {
      text: 'Gerenciar Domínios',
      icon: <DomainIcon />,
      path: '/admin/domains',
      roles: ['admin', 'mensure']
    },
    {
      text: 'Gerenciar Cache',
      icon: <StorageIcon />,
      path: '/admin/cache',
      roles: ['admin', 'mensure']
    },
    {
      text: 'Gerenciar CPC',
      icon: <TrendingUpIcon />,
      path: '/admin/cpc',
      roles: ['admin', 'mensure']
    },
    {
      text: 'Cache Seguidores',
      icon: <FollowersIcon />,
      path: '/admin/profile-followers',
      roles: ['admin', 'mensure']
    },
    {
      text: 'Taxa Visualizacao',
      icon: <VisibilityIcon />,
      path: '/admin/story-view-rate',
      roles: ['admin', 'mensure']
    },
    {
      text: 'Configurar Scrapers',
      icon: <ScraperIcon />,
      path: '/admin/scraper-config',
      roles: ['admin', 'mensure']
    },
    {
      text: 'Analise Sentimento',
      icon: <SentimentIcon />,
      path: '/admin/sentiment-analysis',
      roles: ['admin', 'mensure']
    },
    {
      text: 'Tokens MIA',
      icon: <KeyIcon />,
      path: '/admin/mia-tokens',
      roles: ['admin', 'mensure']
    },
    {
      text: 'Base Global Fontes',
      icon: <GlobalIcon />,
      path: '/admin/global-sources',
      roles: ['admin', 'mensure']
    },
    {
      text: 'Cadastro de Fontes',
      icon: <SourceIcon />,
      path: '/admin/source-registry',
      roles: ['admin', 'mensure']
    },
    {
      text: 'Base de Artigos',
      icon: <AssessmentIcon />,
      path: '/admin/global-articles',
      roles: ['admin', 'mensure']
    },
    {
      text: 'Gerenciar Tiers',
      icon: <TierIcon />,
      path: '/admin/tiers',
      roles: ['admin', 'mensure']
    },
    {
      text: 'Categorias',
      icon: <CategoryIcon />,
      path: '/admin/categories',
      roles: ['admin', 'mensure']
    },
    {
      text: 'Marcas',
      icon: <BrandIcon2 />,
      path: '/admin/brands',
      roles: ['admin', 'mensure']
    },
    {
      text: 'Templates Newsletter',
      icon: <PaletteIcon />,
      path: '/admin/templates',
      roles: ['admin', 'mensure']
    },
    {
      text: 'Analytics',
      icon: <AssessmentIcon />,
      path: '/admin/analytics',
      roles: ['admin', 'mensure']
    },
    {
      text: 'Usuários',
      icon: <PeopleIcon />,
      path: '/admin/users',
      roles: ['admin']
    },
    {
      text: 'Configurações',
      icon: <SettingsIcon />,
      path: '/admin/config',
      roles: ['admin', 'mensure']
    }
  ];

  const handleNavigation = (path) => {
    navigate(path);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const filteredMenuItems = menuItems.filter(item =>
    item.roles.includes(user?.role)
  );

  return (
    <Drawer
      variant="permanent"
      sx={{
        width: 0, // Root não ocupa largura; apenas o paper
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: open ? drawerWidth : miniDrawerWidth,
          boxSizing: 'border-box',
          transition: 'width 0.3s ease',
          backgroundColor: 'background.paper',
          borderRight: 1,
          borderColor: 'divider',
          overflowX: 'hidden',
          zIndex: 1200,
          top: 64, // Posicionar abaixo do AppBar
          height: 'calc(100% - 64px)', // Altura total menos a altura do AppBar
          left: 0, // Posicionar na esquerda
          position: 'fixed' // Fixar posição
        },
      }}
    >
      <Box sx={{ 
        overflowX: 'hidden', 
        overflowY: 'auto', 
        pt: 0,
        height: '100%',
        '&::-webkit-scrollbar': {
          width: '4px',
        },
        '&::-webkit-scrollbar-thumb': {
          backgroundColor: 'rgba(0,0,0,0.1)',
          borderRadius: '4px',
        },
      }}>
        {/* User Info Section */}
        {open ? (
          <Box sx={{ p: 1, borderBottom: 1, borderColor: 'divider' }}>
            <Box display="flex" alignItems="center" gap={2} mb={2}>
              <Avatar
                sx={{
                  bgcolor: 'primary.main',
                  width: 40,
                  height: 40
                }}
              >
                {user?.name?.charAt(0)?.toUpperCase() || 'U'}
              </Avatar>
              <Box>
                <Typography variant="subtitle1" fontWeight="medium">
                  {user?.name || 'Usuário'}
                </Typography>
                <Chip
                  label={user?.role === 'admin' ? 'Administrador' :
                    user?.role === 'mensure' ? 'Mensure' : 'Cliente'}
                  size="small"
                  color={user?.role === 'admin' ? 'error' :
                    user?.role === 'mensure' ? 'warning' : 'info'}
                />
              </Box>
            </Box>
          </Box>
        ) : (
          <Box sx={{ p: 0.5, borderBottom: 1, borderColor: 'divider', display: 'flex', justifyContent: 'center' }}>
            <Tooltip title={`${user?.name || 'Usuário'} - ${user?.role === 'admin' ? 'Administrador' :
              user?.role === 'mensure' ? 'Mensure' : 'Cliente'}`} placement="right">
              <Avatar
                sx={{
                  bgcolor: 'primary.main',
                  width: 32,
                  height: 32
                }}
              >
                {user?.name?.charAt(0)?.toUpperCase() || 'U'}
              </Avatar>
            </Tooltip>
          </Box>
        )}

        <List>
          {filteredMenuItems.map((item) => (
            <ListItem key={item.text} disablePadding>
              {open ? (
                <ListItemButton
                  selected={location.pathname === item.path}
                  onClick={() => handleNavigation(item.path)}
                  sx={{
                    minHeight: 48,
                    justifyContent: 'initial',
                    px: 1,
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: 0,
                      mr: 3,
                      justifyContent: 'center',
                    }}
                  >
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText primary={item.text} sx={{ opacity: 1 }} />
                </ListItemButton>
              ) : (
                <Tooltip title={item.text} placement="right">
                  <ListItemButton
                    selected={location.pathname === item.path}
                    onClick={() => handleNavigation(item.path)}
                    sx={{
                      minHeight: 48,
                      justifyContent: 'center',
                      px: 1,
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        minWidth: 0,
                        mr: 'auto',
                        justifyContent: 'center',
                      }}
                    >
                      {item.icon}
                    </ListItemIcon>
                  </ListItemButton>
                </Tooltip>
              )}
            </ListItem>
          ))}

          <Divider sx={{ my: 1 }} />

          <ListItem disablePadding>
            {open ? (
              <ListItemButton onClick={handleLogout}>
                <ListItemIcon>
                  <LogoutIcon />
                </ListItemIcon>
                <ListItemText primary="Sair" />
              </ListItemButton>
            ) : (
              <Tooltip title="Sair" placement="right">
                <ListItemButton
                  onClick={handleLogout}
                  sx={{
                    minHeight: 48,
                    justifyContent: 'center',
                    px: 1,
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: 0,
                      mr: 'auto',
                      justifyContent: 'center',
                    }}
                  >
                    <LogoutIcon />
                  </ListItemIcon>
                </ListItemButton>
              </Tooltip>
            )}
          </ListItem>
        </List>
      </Box>
    </Drawer>
  );
};

export default AdminNavigation;
